import { AlertFactory } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import { WorkItem } from 'jobs/bookkeeping/BookkeepingProgressionInfo'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import { isNil } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { required, validationContext } from 'validx'

export class CompletedWorkItemViewModel {
  @observable workItemId = Id.none
  @observable inventoryItemName = ''
  @observable inventoryItemDescription = ''
  @observable remaining = 0
  @observable completedItems = 0

  readonly fieldLengths = {
    completedItems: { min: 0 },
  } as const

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    completedItems: [
      required(),
      () =>
        this.completedItemsAreValid ||
        `The number of completed items must be between ${this.fieldLengths.completedItems.min} and ${this.remaining}.`,
    ],
  })

  /**
   * Whether the completed items quantity is valid
   */
  @computed
  get completedItemsAreValid() {
    return (
      this.completedItems <= this.remaining &&
      this.completedItems >= this.fieldLengths.completedItems.min
    )
  }

  constructor(workItem: WorkItem) {
    makeObservable(this)

    this.workItemId = workItem.workItemId
    this.inventoryItemName = workItem.inventoryItemName
    this.remaining = workItem.remaining
    this.inventoryItemDescription = workItem.inventoryItemDescription
  }

  /**
   * Resets the fields
   */
  @action.bound
  reset() {
    this.completedItems = 1
  }

  /**
   * Sets the number of completed items
   * @param value
   */
  @action.bound
  setCompletedItems(value: number | null) {
    if (!isNil(value)) {
      this.completedItems = value
    }
  }
}

export default class BookkeepingCompleteCycleDialogViewModel extends ModalViewModel {
  constructor(
    private newAlert: AlertFactory,
    private jobId: Id,
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Returns the bookkeeping progression info
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this.bookkeepingProgressionStore.bookkeepingProgressions.get(
      this.jobId,
    )
  }

  /**
   * View models for service items to complete in the current cycle
   */
  @computed
  get itemsToComplete() {
    return (
      this.bookkeepingProgressionInfo?.workItems
        .filter((wi) => wi.remaining > 0)
        .map((wi) => new CompletedWorkItemViewModel(wi)) || []
    )
  }

  @computed
  get completedItems() {
    return this.itemsToComplete.filter((itc) => itc.completedItems > 0)
  }

  /**
   * Validates each work item
   * @returns
   */
  @action.bound
  validate() {
    return this.itemsToComplete.every(
      (ci) => ci.validation.reset().validate().isValid,
    )
  }

  /**
   * Resets each work item
   */
  @action.bound
  reset() {
    this.itemsToComplete.forEach((ci) => ci.reset())
  }

  /**
   * Completes the current cycle
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    if (!this.completedItems.length) {
      this.newAlert().fromError('There are no items to complete').show()
      return
    }

    try {
      if (this.bookkeepingProgressionInfo?.currentCycle) {
        await this.bookkeepingProgressionStore.completeBookkeepingCycle({
          jobId: this.jobId,
          cycleId: this.bookkeepingProgressionInfo.currentCycle.id,
          completedItems: this.completedItems.map((ci) => ({
            id: ci.workItemId,
            completedCount: ci.completedItems,
          })),
        })
      } else {
        this.newAlert()
          .fromError('There is no cycle currently in progress')
          .show()
      }

      this.reset()
      this.close()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
